/**
 * SKU to Core Model mapping utility
 *
 * This utility maps device SKU numbers to their corresponding Core model types.
 * The mapping is derived from the Storyline slide 1.2 logic.
 *
 * @see storyline-to-react/PLAN.md Appendix B for the full SKU list
 */

import type { CoreModel } from './coreModel';

// Device model types supported by the installation guide - see CoreModel in ./coreModel
export type { CoreModel };

/**
 * Complete SKU to model mapping
 *
 * Source: Storyline slide 1.2 trigger conditions
 */
const SKU_TO_MODEL: Record<string, CoreModel> = {
  // Lite SKUs (12 entries)
  '101382': 'Lite',
  '101383': 'Lite',
  '101384': 'Lite',
  '101385': 'Lite',
  '101447': 'Lite',
  '101439': 'Lite',
  '101444': 'Lite',
  '101442': 'Lite',
  '101789': 'Lite',
  '101794': 'Lite',
  '101793': 'Lite',
  '101792': 'Lite',

  // Pro SKUs (17 entries)
  '101386': 'Pro',
  '101387': 'Pro',
  '101388': 'Pro',
  '101791': 'Pro',
  '101795': 'Pro',
  '101438': 'Pro',
  '101440': 'Pro',
  '101471': 'Pro',
  '102241': 'Pro',
  '102242': 'Pro',
  '123456': 'Pro',
  '101389': 'Pro',
  '101701': 'Pro',
  '101800': 'Pro',
  '101446': 'Pro',
  '101392': 'Pro',
  '101393': 'Pro',

  // Elite_AI SKUs (11 entries)
  // Note: Legacy "Elite" SKUs now map to Elite_AI per hardware spec
  '101699': 'Elite_AI',
  '101700': 'Elite_AI',
  '101410': 'Elite_AI',
  '101437': 'Elite_AI',
  '101445': 'Elite_AI',
  '101390': 'Elite_AI',
  '101391': 'Elite_AI',
  '101796': 'Elite_AI',
  '101797': 'Elite_AI',
  '101394': 'Elite_AI',
  '101395': 'Elite_AI',

  // Mobile_AI SKUs (per Axon Edge Hardware Identity Provisioning spec, FC1004/F14)
  '103032': 'Mobile_AI',
};

const hasOwnSku = (sku: string): boolean =>
  Object.prototype.hasOwnProperty.call(SKU_TO_MODEL, sku);

/**
 * Get the Core model type from a device SKU
 *
 * @param sku - The device SKU number (can be null)
 * @returns The corresponding CoreModel, or null if SKU is unknown
 *
 * @example
 * getModelFromSku('101382') // returns 'Lite'
 * getModelFromSku('101386') // returns 'Pro'
 * getModelFromSku('101699') // returns 'Elite_AI'
 * getModelFromSku('999999') // returns null (unknown SKU)
 * getModelFromSku(null)     // returns null
 */
export function getModelFromSku(sku: string | null): CoreModel | null {
  if (!sku) return null;
  if (!hasOwnSku(sku)) return null;
  return SKU_TO_MODEL[sku];
}

/**
 * Check if a SKU is recognized
 *
 * @param sku - The device SKU number to check
 * @returns True if the SKU maps to a known model
 */
export function isKnownSku(sku: string | null): boolean {
  if (!sku) return false;
  return hasOwnSku(sku);
}
